import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getMergedSettings,
  updateSettings,
  resetSettingsCategory,
  listServiceTokens,
  setServiceToken,
  deleteServiceToken,
  listPersonalAccessTokens,
  createPersonalAccessToken,
  revokePersonalAccessToken,
  getAuthDBLinkStatus,
  unlinkAuthDB,
  getAuthDBLinkURL,
  getGitHubLinkStatus,
  unlinkGitHub,
  getGitHubLinkURL,
  getGitLabLinkStatus,
  unlinkGitLab,
  getGitLabLinkURL,
  getGoogleDriveLinkStatus,
  unlinkGoogleDrive,
  getGoogleDriveLinkURL,
  getGoogleDriveScopes,
  updateGoogleDriveScopes,
  getSlackLinkStatus,
  unlinkSlack,
  getSlackLinkURL,
  getSlackScopes,
  updateSlackScopes,
  getDynamicsLinkStatus,
  unlinkDynamics,
  getDynamicsLinkURL,
  getPowerAppsLinkStatus,
  unlinkPowerApps,
  getPowerAppsLinkURL,
  type MergedSettings,
  type ServiceToken,
  type PersonalAccessToken,
  type AuthDBLinkStatus,
  type GitHubLinkStatus,
  type GitLabLinkStatus,
  type GoogleDriveLinkStatus,
  type GoogleWorkspaceScope,
  type SlackLinkStatus,
  type SlackScope,
  type DynamicsLinkStatus,
  type PowerAppsLinkStatus,
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreference
} from '../lib/api'
import { useSettings } from '../hooks/useSettings'
import { useUser } from '../hooks/useUser'
import { syncThemeToStorage } from '../hooks/useThemeEffect'
import { usePushSubscription } from '../hooks/usePushSubscription'
import { isIOS } from '../lib/pwaUtils'
import { RotateCcw, Save, ChevronDown, ChevronRight, Terminal, Globe, Layout, Lock, Check, Key, X, Link, Unlink, Copy, Bell, AlertTriangle, Smartphone } from 'lucide-react'
import { SiJira, SiConfluence, SiGithub, SiGitlab, SiGoogle, SiGooglegemini, SiSlack, SiGoogledrive } from 'react-icons/si'
import { FaMicrosoft } from 'react-icons/fa'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { Switch } from '../components/ui/Switch'

function BambooHRIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="33.13" cy="30.687" r="9.37" />
      <path d="m23.757 30.63l.071-22.687m-10.223 3.14c4.192 1.818 5.22 7.141 6.266 11.248c-2.561 1.81-10.287-2.452-14.371-9.6c4.174.573 8.603 2.21 14.42 9.63" />
    </svg>
  )
}

interface CategoryConfig {
  id: string
  name: string
  icon: React.ReactNode
  fields: FieldConfig[]
}

interface FieldConfig {
  key: string
  label: string
  type: 'text' | 'number' | 'boolean' | 'select' | 'multiselect'
  options?: { value: string; label: string }[]
  min?: number
  max?: number
  adminOnly?: boolean
}

const CATEGORIES: CategoryConfig[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: <Layout className="w-5 h-5" />,
    fields: [
      { key: 'theme', label: 'Theme', type: 'select', options: [
        { value: 'system', label: 'System' },
        { value: 'dark', label: 'Dark' },
        { value: 'light', label: 'Light' },
      ]},
      { key: 'home_sections', label: 'Home Page Sections', type: 'multiselect', options: [
        { value: 'show_stats', label: 'Stats Cards' },
        { value: 'show_recent_apps', label: 'Recent Apps' },
        { value: 'show_quick_tips', label: 'Quick Tips' },
        { value: 'show_popular_apps', label: 'Popular Apps' },
      ]},
      { key: 'whats_new_show_on_login', label: "Show What's New on Login", type: 'boolean' },
      { key: 'claude_panel_enabled', label: 'Claude Panel (replaces terminal)', type: 'boolean' },
    ]
  },
  {
    id: 'terminal',
    name: 'Terminal',
    icon: <Terminal className="w-5 h-5" />,
    fields: [
      { key: 'shell', label: 'Shell', type: 'select', options: [
        { value: 'bash', label: 'Bash' },
        { value: 'zsh', label: 'Zsh' },
        { value: 'fish', label: 'Fish' },
      ]},
      { key: 'font_size', label: 'Font Size', type: 'number', min: 10, max: 24 },
      { key: 'theme', label: 'Theme', type: 'select', options: [
        { value: 'dark', label: 'Dark' },
        { value: 'light', label: 'Light' },
      ]},
      { key: 'scrollback_lines', label: 'Scrollback Lines', type: 'number', min: 1000, max: 100000 },
      { key: 'cursor_style', label: 'Cursor Style', type: 'select', options: [
        { value: 'block', label: 'Block' },
        { value: 'underline', label: 'Underline' },
        { value: 'bar', label: 'Bar' },
      ]},
    ]
  },
  {
    id: 'browser',
    name: 'Browser',
    icon: <Globe className="w-5 h-5" />,
    fields: [
      { key: 'homepage', label: 'Homepage', type: 'text' },
      { key: 'dark_mode', label: 'Force Dark Mode', type: 'boolean' },
      { key: 'zoom_level', label: 'Zoom Level (%)', type: 'number', min: 75, max: 150 },
    ]
  },
]

const PAT_EXPIRATION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
  { value: '', label: 'Never expires' },
]

function getGitLabInstanceLabel(status: GitLabLinkStatus | null): string {
  if (status?.gitlab_host?.trim()) {
    return status.gitlab_host.trim()
  }

  if (status?.instance_url?.trim()) {
    try {
      return new URL(status.instance_url).host || 'configured GitLab instance'
    } catch {
      return status.instance_url
    }
  }

  return 'configured GitLab instance'
}

function getGitLabAccountLabel(status: GitLabLinkStatus | null): string {
  const username = status?.gitlab_username?.trim()
  if (username) {
    return `@${username}`
  }

  const name = status?.gitlab_name?.trim()
  if (name) {
    return name
  }

  const email = status?.gitlab_email?.trim()
  if (email) {
    return email
  }

  return 'GitLab account'
}

const USER_NOTIFICATION_TYPES: { type: string; label: string; highPriority?: boolean }[] = [
  { type: 'app_shared', label: 'App shared with you' },
  { type: 'access_request', label: 'Access request on your app' },
  { type: 'access_approved', label: 'Access request approved' },
  { type: 'access_denied', label: 'Access request denied' },
  { type: 'production_build_triggered', label: 'App publish started' },
  { type: 'production_build_success', label: 'App published successfully' },
  { type: 'production_build_failed', label: 'App publish failed', highPriority: true },
  { type: 'app_error', label: 'App error', highPriority: true },
  { type: 'app_auto_stopped', label: 'App auto-stopped' },
  { type: 'feedback_comment', label: 'Comment on your feedback' },
  { type: 'feedback_status', label: 'Feedback status changed' },
]

const ADMIN_NOTIFICATION_TYPES: { type: string; label: string; highPriority?: boolean }[] = [
  { type: 'new_user_registered', label: 'New user registered' },
  { type: 'secret_scan_finding', label: 'Secret scan finding', highPriority: true },
  { type: 'new_feedback', label: 'New feedback submitted' },
]

const ADMIN_MONITORING_TYPES: { type: string; label: string; description: string; highPriority?: boolean }[] = [
  { type: 'other_app_errors', label: "App errors on other users' apps", description: "Get notified when apps you don't own encounter errors", highPriority: true },
]

export default function Settings() {
  // Context for syncing theme changes across the app
  const { refreshSettings: refreshContextSettings } = useSettings()
  const user = useUser()
  const [searchParams, setSearchParams] = useSearchParams()

  const [settings, setSettings] = useState<MergedSettings | null>(null)
  const [serviceTokens, setServiceTokens] = useState<ServiceToken[]>([])
  const [tokenInputs, setTokenInputs] = useState<Record<string, string>>({})
  const [showJira, setShowJira] = useState(false)
  const [showConfluence, setShowConfluence] = useState(false)
  const [showBamboo, setShowBamboo] = useState(false)
  const [showGemini, setShowGemini] = useState(false)
  const [personalAccessTokens, setPersonalAccessTokens] = useState<PersonalAccessToken[]>([])
  const [showPATs, setShowPATs] = useState(false)
  const [newPATName, setNewPATName] = useState('')
  const [newPATExpiresInDays, setNewPATExpiresInDays] = useState('30')
  const [newPATValue, setNewPATValue] = useState<string | null>(null)
  const [tokenCopied, setTokenCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['dashboard']))
  const [editedSettings, setEditedSettings] = useState<Record<string, Record<string, unknown>>>({})
  const [authDBStatus, setAuthDBStatus] = useState<AuthDBLinkStatus | null>(null)
  const [showInternalAccount, setShowInternalAccount] = useState(false)
  const [gitHubStatus, setGitHubStatus] = useState<GitHubLinkStatus | null>(null)
  const [showGitHub, setShowGitHub] = useState(false)
  const [gitLabStatus, setGitLabStatus] = useState<GitLabLinkStatus | null>(null)
  const [showGitLab, setShowGitLab] = useState(false)
  const [googleDriveStatus, setGoogleDriveStatus] = useState<GoogleDriveLinkStatus | null>(null)
  const [showGoogleDrive, setShowGoogleDrive] = useState(false)
  const [googleScopes, setGoogleScopes] = useState<GoogleWorkspaceScope[]>([])
  const [googleScopesEdited, setGoogleScopesEdited] = useState<Record<string, boolean>>({})
  const [slackStatus, setSlackStatus] = useState<SlackLinkStatus | null>(null)
  const [showSlack, setShowSlack] = useState(false)
  const [slackScopes, setSlackScopes] = useState<SlackScope[]>([])
  const [slackScopesEdited, setSlackScopesEdited] = useState<Record<string, boolean>>({})
  const [dynamicsStatus, setDynamicsStatus] = useState<DynamicsLinkStatus | null>(null)
  const [showDynamics, setShowDynamics] = useState(false)
  const [powerAppsStatus, setPowerAppsStatus] = useState<PowerAppsLinkStatus | null>(null)
  const [showPowerApps, setShowPowerApps] = useState(false)
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({})
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifSaved, setNotifSaved] = useState(false)
  const [notifSaving, setNotifSaving] = useState(false)

  const {
    isSupported: pushSupported,
    isSubscribed: pushEnabled,
    isLoading: pushLoading,
    permission: pushPermission,
    subscribe: enablePush,
    unsubscribe: disablePush,
    error: pushError,
    needsInstallFirst: pushNeedsInstall,
  } = usePushSubscription()

  useEffect(() => {
    loadSettings()
  }, [])

  // Handle URL params for AuthDB linking feedback
  useEffect(() => {
    const authdbLinked = searchParams.get('authdb_linked')
    const authdbError = searchParams.get('authdb_error')

    if (authdbLinked === 'true') {
      setSuccess('Internal account linked successfully')
      setShowInternalAccount(true)
      // Clear URL params
      searchParams.delete('authdb_linked')
      setSearchParams(searchParams, { replace: true })
      // Reload AuthDB status
      getAuthDBLinkStatus().then(setAuthDBStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (authdbError) {
      setError(`Failed to link internal account: ${authdbError}`)
      setShowInternalAccount(true)
      // Clear URL params
      searchParams.delete('authdb_error')
      setSearchParams(searchParams, { replace: true })
    }

    const githubLinked = searchParams.get('github_linked')
    const githubError = searchParams.get('github_error')

    if (githubLinked === 'true') {
      setSuccess('GitHub account connected successfully')
      setShowGitHub(true)
      searchParams.delete('github_linked')
      setSearchParams(searchParams, { replace: true })
      getGitHubLinkStatus().then(setGitHubStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (githubError) {
      setError(`Failed to connect GitHub: ${githubError}`)
      setShowGitHub(true)
      searchParams.delete('github_error')
      setSearchParams(searchParams, { replace: true })
    }

    const gitlabLinked = searchParams.get('gitlab_linked')
    const gitlabError = searchParams.get('gitlab_error')

    if (gitlabLinked === 'true') {
      setSuccess('GitLab account connected successfully')
      setShowGitLab(true)
      searchParams.delete('gitlab_linked')
      setSearchParams(searchParams, { replace: true })
      getGitLabLinkStatus().then(setGitLabStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (gitlabError) {
      setError(`Failed to connect GitLab: ${gitlabError}`)
      setShowGitLab(true)
      searchParams.delete('gitlab_error')
      setSearchParams(searchParams, { replace: true })
    }

    const googleDriveLinked = searchParams.get('google_drive_linked')
    const googleDriveError = searchParams.get('google_drive_error')

    if (googleDriveLinked === 'true') {
      setSuccess('Google Workspace connected successfully')
      setShowGoogleDrive(true)
      searchParams.delete('google_drive_linked')
      setSearchParams(searchParams, { replace: true })
      getGoogleDriveLinkStatus().then(setGoogleDriveStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (googleDriveError) {
      setError(`Failed to connect Google Workspace: ${googleDriveError}`)
      setShowGoogleDrive(true)
      searchParams.delete('google_drive_error')
      setSearchParams(searchParams, { replace: true })
    }

    const slackLinked = searchParams.get('slack_linked')
    const slackError = searchParams.get('slack_error')

    if (slackLinked === 'true') {
      setSuccess('Slack workspace connected successfully')
      setShowSlack(true)
      searchParams.delete('slack_linked')
      setSearchParams(searchParams, { replace: true })
      getSlackLinkStatus().then(setSlackStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (slackError) {
      setError(`Failed to connect Slack: ${slackError}`)
      setShowSlack(true)
      searchParams.delete('slack_error')
      setSearchParams(searchParams, { replace: true })
    }

    const dynamicsLinked = searchParams.get('dynamics_linked')
    const dynamicsError = searchParams.get('dynamics_error')

    if (dynamicsLinked === 'true') {
      setSuccess('Dynamics Business Central connected successfully')
      setShowDynamics(true)
      searchParams.delete('dynamics_linked')
      setSearchParams(searchParams, { replace: true })
      getDynamicsLinkStatus().then(setDynamicsStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (dynamicsError) {
      setError(`Failed to connect Dynamics: ${dynamicsError}`)
      setShowDynamics(true)
      searchParams.delete('dynamics_error')
      setSearchParams(searchParams, { replace: true })
    }

    const powerappsLinked = searchParams.get('powerapps_linked')
    const powerappsError = searchParams.get('powerapps_error')

    if (powerappsLinked === 'true') {
      setSuccess('PowerApps connected successfully')
      setShowPowerApps(true)
      searchParams.delete('powerapps_linked')
      setSearchParams(searchParams, { replace: true })
      getPowerAppsLinkStatus().then(setPowerAppsStatus)
      setTimeout(() => setSuccess(null), 5000)
    }

    if (powerappsError) {
      setError(`Failed to connect PowerApps: ${powerappsError}`)
      setShowPowerApps(true)
      searchParams.delete('powerapps_error')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  async function loadSettings() {
    try {
      const [settingsData, tokensData, patsData, authdbData, githubData, gitlabData, googleDriveData, scopesData, slackData, slackScopesData, dynamicsData, powerAppsData, notifPrefsData] = await Promise.all([
        getMergedSettings(),
        listServiceTokens().catch(() => ({ tokens: [] })),
        listPersonalAccessTokens().catch(() => []),
        getAuthDBLinkStatus().catch(() => ({ linked: false, service: 'authdb' })),
        getGitHubLinkStatus().catch(() => ({ linked: false, service: 'github' })),
        getGitLabLinkStatus().catch(() => ({ linked: false, service: 'gitlab' })),
        getGoogleDriveLinkStatus().catch(() => ({ linked: false, service: 'google-drive' })),
        getGoogleDriveScopes().catch(() => [] as GoogleWorkspaceScope[]),
        getSlackLinkStatus().catch(() => ({ linked: false, service: 'slack' })),
        getSlackScopes().catch(() => [] as SlackScope[]),
        getDynamicsLinkStatus().catch(() => ({ linked: false, service: 'dynamics' })),
        getPowerAppsLinkStatus().catch(() => ({ linked: false, service: 'powerapps' })),
        getNotificationPreferences().catch(() => [] as NotificationPreference[]),
      ])
      setSettings(settingsData)
      setServiceTokens(tokensData.tokens)
      setPersonalAccessTokens(patsData)
      setAuthDBStatus(authdbData)
      setGitHubStatus(githubData)
      setGitLabStatus(gitlabData)
      setGoogleDriveStatus(googleDriveData)
      setGoogleScopes(scopesData)
      setGoogleScopesEdited(Object.fromEntries(scopesData.map((s: GoogleWorkspaceScope) => [s.key, s.enabled])))
      setSlackStatus(slackData)
      setSlackScopes(slackScopesData)
      setSlackScopesEdited(Object.fromEntries(slackScopesData.map((s: SlackScope) => [s.key, s.enabled])))
      setDynamicsStatus(dynamicsData)
      setPowerAppsStatus(powerAppsData)
      setNotifPrefs(Object.fromEntries(notifPrefsData.map((p: NotificationPreference) => [p.type, p.enabled])))
      setEditedSettings({
        dashboard: settingsData.dashboard as Record<string, unknown>,
        terminal: settingsData.terminal as Record<string, unknown>,
        browser: settingsData.browser as Record<string, unknown>,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  function formatTimestamp(value: string | null, emptyLabel: string = 'Never'): string {
    return value ? new Date(value).toLocaleString() : emptyLabel
  }

  async function handleCreatePAT() {
    const name = newPATName.trim()
    if (!name) return

    const expiresInDays = newPATExpiresInDays ? Number(newPATExpiresInDays) : null
    if (newPATExpiresInDays && (!Number.isInteger(expiresInDays) || Number(expiresInDays) <= 0)) {
      setError('Expiration must be a whole number greater than 0 days')
      return
    }

    setSaving('pat-create')
    setError(null)
    setSuccess(null)
    try {
      const created = await createPersonalAccessToken({
        name,
        expires_in_days: expiresInDays,
      })
      const patsData = await listPersonalAccessTokens()
      setPersonalAccessTokens(patsData)
      setNewPATName('')
      setNewPATExpiresInDays('30')
      setNewPATValue(created.token)
      setTokenCopied(false)
      setSuccess('API access token created')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token')
    } finally {
      setSaving(null)
    }
  }

  async function handleCopyNewPAT() {
    if (!newPATValue) return
    try {
      await navigator.clipboard.writeText(newPATValue)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 2000)
    } catch {
      setError('Failed to copy token')
    }
  }

  function toggleCategory(categoryId: string) {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId)
    } else {
      newExpanded.add(categoryId)
    }
    setExpandedCategories(newExpanded)
  }

  function handleFieldChange(categoryId: string, key: string, value: unknown) {
    setEditedSettings({
      ...editedSettings,
      [categoryId]: {
        ...editedSettings[categoryId],
        [key]: value
      }
    })

    // Live preview for theme changes
    if (categoryId === 'dashboard' && key === 'theme') {
      syncThemeToStorage(value as string)
      const resolved = value === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : value as string
      document.documentElement.classList.remove('light', 'dark')
      document.documentElement.classList.add(resolved)
    }
  }

  function hasChanges(categoryId: string): boolean {
    if (!settings) return false
    const settingsAny = settings as unknown as Record<string, unknown>
    const original = settingsAny[categoryId] as Record<string, unknown>
    const edited = editedSettings[categoryId]
    return JSON.stringify(original) !== JSON.stringify(edited)
  }

  async function handleSave(categoryId: string) {
    setSaving(categoryId)
    setError(null)
    setSuccess(null)

    try {
      await updateSettings({ [categoryId]: editedSettings[categoryId] })
      await loadSettings()

      // Sync theme to localStorage if dashboard settings changed
      if (categoryId === 'dashboard') {
        const theme = editedSettings[categoryId]?.theme as string
        if (theme) {
          syncThemeToStorage(theme)
        }
      }

      // Always refresh context so other components get the updated settings
      // This ensures terminal font size, editor font size, etc. are synced
      await refreshContextSettings()

      setSuccess(`${categoryId} settings saved`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(null)
    }
  }

  async function handleReset(categoryId: string) {
    setSaving(categoryId)
    setError(null)

    try {
      await resetSettingsCategory(categoryId)
      await loadSettings()

      // Sync theme if dashboard settings were reset
      if (categoryId === 'dashboard') {
        // After reset, the new theme is in the reloaded settings
        const newSettings = await getMergedSettings()
        const theme = (newSettings.dashboard as Record<string, unknown>)?.theme as string
        if (theme) {
          syncThemeToStorage(theme)
          const resolved = theme === 'system'
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : theme
          document.documentElement.classList.remove('light', 'dark')
          document.documentElement.classList.add(resolved)
        }
      }

      // Always refresh context so other components get the updated settings
      await refreshContextSettings()

      setSuccess(`${categoryId} settings reset to defaults`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings')
    } finally {
      setSaving(null)
    }
  }

  async function handleNotifToggle(type: string, enabled: boolean) {
    const updated = { ...notifPrefs, [type]: enabled }
    setNotifPrefs(updated)
    setNotifSaving(true)
    setNotifSaved(false)
    try {
      const prefs = Object.entries(updated).map(([t, e]) => ({ type: t, enabled: e }))
      await updateNotificationPreferences(prefs)
      setNotifSaved(true)
      setTimeout(() => setNotifSaved(false), 2000)
    } catch (err) {
      // Revert on failure
      setNotifPrefs(notifPrefs)
      setError(err instanceof Error ? err.message : 'Failed to save notification preferences')
    } finally {
      setNotifSaving(false)
    }
  }

  function renderField(categoryId: string, field: FieldConfig) {
    const value = editedSettings[categoryId]?.[field.key]
    const inputClasses = "w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"

    switch (field.type) {
      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleFieldChange(categoryId, field.key, e.target.checked)}
              disabled={field.adminOnly}
              className="w-4 h-4 rounded border-border"
            />
            <span className="text-sm">{field.label}</span>
            {field.adminOnly && <Lock className="w-3 h-3 text-muted-foreground" />}
          </label>
        )

      case 'select':
        return (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-1">
              {field.label}
              {field.adminOnly && <Lock className="w-3 h-3 text-muted-foreground" />}
            </label>
            <select
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(categoryId, field.key, e.target.value)}
              disabled={field.adminOnly}
              className={inputClasses + " max-w-xs"}
            >
              {field.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )

      case 'multiselect':
        return (
          <div>
            <label className="text-sm font-medium mb-2 block">{field.label}</label>
            <div className="flex flex-wrap gap-2">
              {field.options?.map((opt) => {
                const checked = editedSettings[categoryId]?.[opt.value] !== false
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleFieldChange(categoryId, opt.value, !checked)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      checked
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-border/80'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        )

      case 'number':
        return (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-1">
              {field.label}
              {field.adminOnly && <Lock className="w-3 h-3 text-muted-foreground" />}
            </label>
            <input
              type="number"
              value={value !== undefined ? Number(value) : ''}
              onChange={(e) => handleFieldChange(categoryId, field.key, Number(e.target.value))}
              min={field.min}
              max={field.max}
              disabled={field.adminOnly}
              className={inputClasses + " w-32"}
            />
          </div>
        )

      default:
        return (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-1">
              {field.label}
              {field.adminOnly && <Lock className="w-3 h-3 text-muted-foreground" />}
            </label>
            <input
              type="text"
              value={String(value ?? '')}
              onChange={(e) => handleFieldChange(categoryId, field.key, e.target.value)}
              disabled={field.adminOnly}
              className={inputClasses + " max-w-md"}
            />
          </div>
        )
    }
  }

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">Customize your workspace preferences</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 rounded-lg bg-green-500/10 text-green-500 border border-green-500/20 flex items-center gap-2">
          <Check className="w-4 h-4" />
          {success}
        </div>
      )}

      <div className="space-y-4">
        {CATEGORIES.map((category) => {
          const isExpanded = expandedCategories.has(category.id)
          const hasUnsavedChanges = hasChanges(category.id)
          const isCustomized = settings.customized_categories.includes(category.id)

          return (
            <div
              key={category.id}
              className="rounded-xl border border-border bg-card overflow-hidden"
            >
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-secondary">
                    {category.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold flex items-center gap-2">
                      {category.name}
                      {isCustomized && (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                          Customized
                        </span>
                      )}
                      {hasUnsavedChanges && (
                        <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-500">
                          Unsaved
                        </span>
                      )}
                    </h3>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </button>

              {isExpanded && (
                <div className="p-4 pt-0 border-t border-border">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 pt-4">
                    {category.fields.map((field) => (
                      <div key={field.key}>
                        {renderField(category.id, field)}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 mt-6 pt-4 border-t border-border">
                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === category.id}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reset to Defaults
                        </button>
                      }
                      title="Reset Settings"
                      description={`Reset ${category.name} settings to platform defaults? This cannot be undone.`}
                      confirmLabel="Reset"
                      variant="destructive"
                      onConfirm={() => handleReset(category.id)}
                    />
                    <button
                      onClick={() => handleSave(category.id)}
                      disabled={!hasUnsavedChanges || saving === category.id}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {saving === category.id ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        <div className="pt-1 border-t border-border/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Integrations</p>
        </div>

        {/* Service Tokens — one card per service */}
        {([
          {
            service: 'jira',
            label: 'JIRA',
            description: 'Personal access token for JIRA issue tracking',
            icon: <SiJira className="w-5 h-5" />,
            linkUrl: 'https://jira.exante.eu/secure/ViewProfile.jspa?selectedTab=com.atlassian.pats.pats-plugin:jira-user-personal-access-tokens',
            show: showJira,
            setShow: setShowJira,
          },
          {
            service: 'confluence',
            label: 'Confluence',
            description: 'Personal access token for Confluence wiki',
            icon: <SiConfluence className="w-5 h-5" />,
            linkUrl: 'https://confluence.exante.eu/plugins/personalaccesstokens/usertokens.action',
            show: showConfluence,
            setShow: setShowConfluence,
          },
          {
            service: 'bamboohr',
            label: 'BambooHR',
            description: 'API key for BambooHR',
            icon: <BambooHRIcon className="w-5 h-5" />,
            linkUrl: 'https://exante.bamboohr.com/app/settings/permissions/api_keys',
            show: showBamboo,
            setShow: setShowBamboo,
          },
          {
            service: 'gemini',
            label: 'Google Gemini',
            // Gemini is a hybrid integration: the platform provides a shared
            // fallback key, but heavy users can set their own to bypass the
            // shared quota. Link points to Service Desk since our IT team
            // provisions personal keys rather than Google directly.
            description: 'Personal API key — overrides the shared platform key and lifts rate limits',
            icon: <SiGooglegemini className="w-5 h-5" />,
            linkUrl: 'https://jira.exante.eu/plugins/servlet/desk/portal/3/create/25',
            show: showGemini,
            setShow: setShowGemini,
          },
        ] as const).map(({ service, label, description, icon, linkUrl, show, setShow }) => {
          const token = serviceTokens.find(t => t.service === service)
          return (
          <div key={service} className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              onClick={() => setShow(!show)}
              className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary">
                  {icon}
                </div>
                <div className="text-left">
                  <h3 className="font-semibold flex items-center gap-2">
                    {label}
                    {token?.configured && (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                        Configured
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {description}
                  </p>
                </div>
              </div>
              {show ? (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              )}
            </button>

            {show && (
              <div className="p-4 pt-0 border-t border-border">
                <div className="space-y-2 pt-4">
                  <label className="text-sm font-medium flex items-center gap-2">
                    {label} Token
                    <a
                      href={linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      Get token
                    </a>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={tokenInputs[service] || ''}
                      onChange={(e) => setTokenInputs({
                        ...tokenInputs,
                        [service]: e.target.value
                      })}
                      placeholder={token?.configured ? '••••••••••••••••' : 'Enter your personal access token'}
                      className="flex-1 max-w-md px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      onClick={async () => {
                        const value = tokenInputs[service]
                        if (!value || !value.trim()) return

                        setSaving(`token-${service}`)
                        setError(null)
                        try {
                          await setServiceToken(service, value.trim())
                          const tokensData = await listServiceTokens()
                          setServiceTokens(tokensData.tokens)
                          setTokenInputs({
                            ...tokenInputs,
                            [service]: ''
                          })
                          setSuccess(`${label} token saved`)
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to save token')
                        } finally {
                          setSaving(null)
                        }
                      }}
                      disabled={!tokenInputs[service]?.trim() || saving === `token-${service}`}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {saving === `token-${service}` ? 'Saving...' : 'Save'}
                    </button>
                    {token?.configured && (
                      <button
                        onClick={async () => {
                          setSaving(`token-clear-${service}`)
                          setError(null)
                          try {
                            await deleteServiceToken(service)
                            const tokensData = await listServiceTokens()
                            setServiceTokens(tokensData.tokens)
                            setSuccess(`${label} token cleared`)
                            setTimeout(() => setSuccess(null), 3000)
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to clear token')
                          } finally {
                            setSaving(null)
                          }
                        }}
                        disabled={saving === `token-clear-${service}`}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Clear
                      </button>
                    )}
                  </div>
                  {token?.configured && token.updated_at && (
                    <p className="text-xs text-muted-foreground">
                      Last updated: {new Date(token.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <p className="mt-4 text-xs text-muted-foreground">
                  Token is stored securely and used by deployed apps to access {label}.
                </p>
              </div>
            )}
          </div>
          )
        })}

        {/* GitHub Account */}
        {gitHubStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowGitHub(!showGitHub)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <SiGithub className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  GitHub
                  {gitHubStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Connect your GitHub account to import repositories
                </p>
              </div>
            </div>
            {showGitHub ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showGitHub && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {gitHubStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">Connected as @{gitHubStatus.github_username}</p>
                        <p className="text-sm text-muted-foreground">
                          Your GitHub account is connected. You can import repositories into workspaces.
                        </p>
                      </div>
                    </div>

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'github-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'github-unlink' ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      }
                      title="Disconnect GitHub"
                      description="You will no longer be able to import private repositories. You can reconnect at any time."
                      confirmLabel="Disconnect"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('github-unlink')
                        setError(null)
                        try {
                          await unlinkGitHub()
                          setGitHubStatus({ linked: false, service: 'github' })
                          setSuccess('GitHub account disconnected')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to disconnect GitHub')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect your GitHub account to browse and import your repositories into workspaces.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        We only request read access to your profile. Additional permissions may be requested when importing repositories.
                      </p>
                    </div>

                    <a
                      href={getGitHubLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <SiGithub className="w-4 h-4" />
                      Connect GitHub
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Connects your GitHub account for repository import. Your access token is encrypted at rest.
              </p>
            </div>
          )}
        </div>
        )}

        {/* GitLab Account */}
        {gitLabStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowGitLab(!showGitLab)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <SiGitlab className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  GitLab
                  {gitLabStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Connect your GitLab account to import repositories from {getGitLabInstanceLabel(gitLabStatus)}
                </p>
              </div>
            </div>
            {showGitLab ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showGitLab && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {gitLabStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">
                          Connected as {getGitLabAccountLabel(gitLabStatus)} on {getGitLabInstanceLabel(gitLabStatus)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Your GitLab account is connected. You can browse and import repositories from this self-hosted instance.
                        </p>
                      </div>
                    </div>

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'gitlab-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'gitlab-unlink' ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      }
                      title="Disconnect GitLab"
                      description={`You will no longer be able to import repositories from ${getGitLabInstanceLabel(gitLabStatus)}. You can reconnect at any time.`}
                      confirmLabel="Disconnect"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('gitlab-unlink')
                        setError(null)
                        try {
                          await unlinkGitLab()
                          setGitLabStatus({ linked: false, service: 'gitlab', instance_url: gitLabStatus?.instance_url, gitlab_host: gitLabStatus?.gitlab_host })
                          setSuccess('GitLab account disconnected')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to disconnect GitLab')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect your GitLab account to browse and import repositories from {getGitLabInstanceLabel(gitLabStatus)} directly into workspaces.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        This uses the configured self-hosted GitLab instance rather than gitlab.com.
                      </p>
                    </div>

                    <a
                      href={getGitLabLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <SiGitlab className="w-4 h-4" />
                      Connect GitLab
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Connects your self-hosted GitLab account for repository import. Your access token is encrypted at rest and refreshed automatically when supported.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Google Workspace */}
        {googleDriveStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowGoogleDrive(!showGoogleDrive)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <SiGoogle className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  Google Workspace
                  {googleDriveStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Connect Google Workspace services for deployed apps
                </p>
              </div>
            </div>
            {showGoogleDrive ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showGoogleDrive && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {googleDriveStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">Connected as {googleDriveStatus.email}</p>
                        <p className="text-sm text-muted-foreground">
                          Google Workspace connected. Deployed apps can access {googleScopes.filter(s => googleScopesEdited[s.key]).map(s => s.label).join(', ') || 'selected services'}.
                        </p>
                      </div>
                    </div>

                    {googleDriveStatus.scopes_changed && (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                        <RotateCcw className="w-5 h-5 text-yellow-500 shrink-0" />
                        <div>
                          <p className="font-medium text-yellow-500">Reconnect Required</p>
                          <p className="text-sm text-muted-foreground">
                            Scope preferences have changed since you last connected. Disconnect and reconnect to apply the new scopes.
                          </p>
                        </div>
                      </div>
                    )}

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'google-drive-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'google-drive-unlink' ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      }
                      title="Disconnect Google Workspace"
                      description={`Deployed apps will no longer be able to access any Google Workspace services (${googleScopes.filter(s => googleScopesEdited[s.key]).map(s => s.label).join(', ')}). You can reconnect at any time.`}
                      confirmLabel="Disconnect"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('google-drive-unlink')
                        setError(null)
                        try {
                          await unlinkGoogleDrive()
                          setGoogleDriveStatus({ linked: false, service: 'google-drive' })
                          setSuccess('Google Workspace disconnected')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to disconnect Google Workspace')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect your Google account to allow deployed apps to access Workspace services: {googleScopes.filter(s => googleScopesEdited[s.key]).map(s => s.label).join(', ') || 'selected services'}.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Only your @exante.eu Google account is supported.
                      </p>
                    </div>

                    <a
                      href={getGoogleDriveLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <SiGoogledrive className="w-4 h-4" />
                      Connect Google Workspace
                    </a>
                  </div>
                )}

                {/* Scope toggles */}
                {googleScopes.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">API Scopes</h4>
                    <div className="space-y-1">
                      {googleScopes.map(scope => (
                        <label
                          key={scope.key}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={googleScopesEdited[scope.key] ?? scope.enabled}
                            disabled={scope.required || saving === 'google-scopes'}
                            onChange={async e => {
                              const updated = { ...googleScopesEdited, [scope.key]: e.target.checked }
                              setGoogleScopesEdited(updated)
                              setSaving('google-scopes')
                              setError(null)
                              try {
                                await updateGoogleDriveScopes(updated)
                                const [newScopes, newStatus] = await Promise.all([
                                  getGoogleDriveScopes(),
                                  getGoogleDriveLinkStatus(),
                                ])
                                setGoogleScopes(newScopes)
                                setGoogleScopesEdited(Object.fromEntries(newScopes.map(s => [s.key, s.enabled])))
                                setGoogleDriveStatus(newStatus)
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Failed to save scope preferences')
                                // Revert the toggle on failure
                                setGoogleScopesEdited(prev => ({ ...prev, [scope.key]: !e.target.checked }))
                              } finally {
                                setSaving(null)
                              }
                            }}
                            className="mt-0.5 rounded border-border"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{scope.label}</span>
                              {scope.required && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Required</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{scope.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>

                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Connects Google Workspace services for use by deployed apps. Your access token is encrypted at rest and refreshed automatically.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Slack */}
        {slackStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowSlack(!showSlack)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <SiSlack className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  Slack
                  {slackStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Connect Slack workspace for deployed apps
                </p>
              </div>
            </div>
            {showSlack ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showSlack && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {slackStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">
                          Connected{slackStatus.team_name ? ` to ${slackStatus.team_name}` : ''}{slackStatus.email ? ` as ${slackStatus.email}` : ''}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Slack connected. Deployed apps can access {slackScopes.filter(s => slackScopesEdited[s.key]).map(s => s.label).join(', ') || 'selected APIs'}.
                        </p>
                      </div>
                    </div>

                    {slackStatus.scopes_changed && (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                        <RotateCcw className="w-5 h-5 text-yellow-500 shrink-0" />
                        <div>
                          <p className="font-medium text-yellow-500">Reconnect Required</p>
                          <p className="text-sm text-muted-foreground">
                            Scope preferences have changed since you last connected. Disconnect and reconnect to apply the new scopes.
                          </p>
                        </div>
                      </div>
                    )}

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'slack-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'slack-unlink' ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      }
                      title="Disconnect Slack"
                      description={`Deployed apps will no longer be able to access Slack APIs (${slackScopes.filter(s => slackScopesEdited[s.key]).map(s => s.label).join(', ')}). You can reconnect at any time.`}
                      confirmLabel="Disconnect"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('slack-unlink')
                        setError(null)
                        try {
                          await unlinkSlack()
                          setSlackStatus({ linked: false, service: 'slack' })
                          setSuccess('Slack disconnected')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to disconnect Slack')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect your Slack workspace to allow deployed apps to access Slack APIs: {slackScopes.filter(s => slackScopesEdited[s.key]).map(s => s.label).join(', ') || 'selected APIs'}.
                      </p>
                    </div>

                    <a
                      href={getSlackLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <SiSlack className="w-4 h-4" />
                      Connect Slack
                    </a>
                  </div>
                )}

                {/* Scope toggles */}
                {slackScopes.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">API Scopes</h4>
                    <div className="space-y-1">
                      {slackScopes.map(scope => (
                        <label
                          key={scope.key}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={slackScopesEdited[scope.key] ?? scope.enabled}
                            disabled={scope.required || saving === 'slack-scopes'}
                            onChange={async e => {
                              const updated = { ...slackScopesEdited, [scope.key]: e.target.checked }
                              setSlackScopesEdited(updated)
                              setSaving('slack-scopes')
                              setError(null)
                              try {
                                await updateSlackScopes(updated)
                                const [newScopes, newStatus] = await Promise.all([
                                  getSlackScopes(),
                                  getSlackLinkStatus(),
                                ])
                                setSlackScopes(newScopes)
                                setSlackScopesEdited(Object.fromEntries(newScopes.map(s => [s.key, s.enabled])))
                                setSlackStatus(newStatus)
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Failed to save scope preferences')
                                setSlackScopesEdited(prev => ({ ...prev, [scope.key]: !e.target.checked }))
                              } finally {
                                setSaving(null)
                              }
                            }}
                            className="mt-0.5 rounded border-border"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{scope.label}</span>
                              {scope.required && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Required</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{scope.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Connects your Slack workspace for use by deployed apps. Your access token is encrypted at rest{slackStatus?.linked && slackStatus.team_name ? ` (${slackStatus.team_name})` : ''}.
              </p>
            </div>
          )}
        </div>
        )}

        {/* MS Dynamics / Business Central */}
        {dynamicsStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowDynamics(!showDynamics)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <FaMicrosoft className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  Dynamics 365 Business Central
                  {dynamicsStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Connect your Microsoft account for Business Central API access
                </p>
              </div>
            </div>
            {showDynamics ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showDynamics && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {dynamicsStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">
                          Connected{dynamicsStatus.ms_display_name ? ` as ${dynamicsStatus.ms_display_name}` : ''}{dynamicsStatus.ms_email ? ` (${dynamicsStatus.ms_email})` : ''}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Business Central environment: {dynamicsStatus.bc_environment || 'default'}
                        </p>
                      </div>
                    </div>

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'dynamics-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'dynamics-unlink' ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      }
                      title="Disconnect Dynamics 365"
                      description="Deployed apps will no longer be able to access Business Central APIs. You can reconnect at any time."
                      confirmLabel="Disconnect"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('dynamics-unlink')
                        setError(null)
                        try {
                          await unlinkDynamics()
                          setDynamicsStatus({ linked: false, service: 'dynamics', configured: true })
                          setSuccess('Dynamics 365 disconnected')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to disconnect Dynamics')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect your Microsoft account to allow deployed apps to access Dynamics 365 Business Central APIs
                        (GL entries, vendors, customers, purchase invoices, and more).
                      </p>
                      <p className="text-sm text-muted-foreground">
                        You will be redirected to Microsoft to sign in and grant access.
                      </p>
                    </div>

                    <a
                      href={getDynamicsLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <FaMicrosoft className="w-4 h-4" />
                      Connect Dynamics 365
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Connects your Microsoft account for Business Central API access. Your access token is encrypted at rest and refreshed automatically.
              </p>
            </div>
          )}
        </div>
        )}

        {/* PowerApps / Dataverse */}
        {powerAppsStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowPowerApps(!showPowerApps)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <FaMicrosoft className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  PowerApps
                  {powerAppsStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Connect your Microsoft account for PowerApps / Dataverse API access
                </p>
              </div>
            </div>
            {showPowerApps ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showPowerApps && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {powerAppsStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">
                          Connected{powerAppsStatus.ms_display_name ? ` as ${powerAppsStatus.ms_display_name}` : ''}{powerAppsStatus.ms_email ? ` (${powerAppsStatus.ms_email})` : ''}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          PowerApps / Dataverse APIs are available to deployed apps.
                        </p>
                      </div>
                    </div>

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'powerapps-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'powerapps-unlink' ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      }
                      title="Disconnect PowerApps"
                      description="Deployed apps will no longer be able to access PowerApps / Dataverse APIs. You can reconnect at any time."
                      confirmLabel="Disconnect"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('powerapps-unlink')
                        setError(null)
                        try {
                          await unlinkPowerApps()
                          setPowerAppsStatus({ linked: false, service: 'powerapps', configured: true })
                          setSuccess('PowerApps disconnected')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to disconnect PowerApps')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Connect your Microsoft account to allow deployed apps to access PowerApps / Dataverse APIs
                        (submissions, invoices, expenses, vendors, and more).
                      </p>
                      <p className="text-sm text-muted-foreground">
                        You will be redirected to Microsoft to sign in and grant access.
                      </p>
                    </div>

                    <a
                      href={getPowerAppsLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <FaMicrosoft className="w-4 h-4" />
                      Connect PowerApps
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Connects your Microsoft account for PowerApps / Dataverse API access. Your access token is encrypted at rest and refreshed automatically.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Internal Account (AuthDB Linking) */}
        {authDBStatus?.configured !== false && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowInternalAccount(!showInternalAccount)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Link className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  Internal Account
                  {authDBStatus?.linked && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Linked
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Link your Exante internal account for CRM and backoffice access
                </p>
              </div>
            </div>
            {showInternalAccount ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showInternalAccount && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4">
                {authDBStatus?.linked ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <Check className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-green-500">Account Linked</p>
                        <p className="text-sm text-muted-foreground">
                          Your internal account is linked. Apps can access CRM, backoffice, and other internal services.
                        </p>
                      </div>
                    </div>

                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === 'authdb-unlink'}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                        >
                          <Unlink className="w-4 h-4" />
                          {saving === 'authdb-unlink' ? 'Unlinking...' : 'Unlink Account'}
                        </button>
                      }
                      title="Unlink Internal Account"
                      description="Apps will no longer be able to access internal services (CRM, backoffice, etc.). You can re-link at any time."
                      confirmLabel="Unlink"
                      variant="destructive"
                      onConfirm={async () => {
                        setSaving('authdb-unlink')
                        setError(null)
                        try {
                          await unlinkAuthDB()
                          setAuthDBStatus({ linked: false, service: 'authdb' })
                          setSuccess('Internal account unlinked')
                          setTimeout(() => setSuccess(null), 3000)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to unlink account')
                        } finally {
                          setSaving(null)
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground mb-4">
                        Link your Exante internal account to enable apps to access internal services like CRM, backoffice, and other company tools.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        This is optional - only needed if you're building or using apps that connect to internal systems.
                      </p>
                    </div>

                    <a
                      href={getAuthDBLinkURL()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <Link className="w-4 h-4" />
                      Link Internal Account
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Links your Exante AuthDB account for accessing internal APIs. Your credentials are stored securely.
              </p>
            </div>
          )}
        </div>
        )}

        <div className="pt-1 border-t border-border/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">API Access</p>
        </div>

        {/* API Access Tokens */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowPATs(!showPATs)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Key className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  Personal Access Tokens
                  {personalAccessTokens.length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      {personalAccessTokens.length} active
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Generate platform API tokens for scripts, CLI tools, and automations
                </p>
              </div>
            </div>
            {showPATs ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showPATs && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="space-y-4 pt-4">
                <div className="p-3 rounded-lg bg-secondary/50 border border-border/60">
                  <p className="text-sm text-muted-foreground">
                    Generate a new API token here. It will be shown once after creation.
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Need setup steps for Swagger and terminal auth?{' '}
                    <a href="/guide/personal-access-tokens" className="text-primary underline underline-offset-2 hover:no-underline">
                      Read the PAT user guide
                    </a>
                    .
                  </p>
                </div>

                {newPATValue && (
                  <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 space-y-3">
                    <p className="text-sm text-amber-400">
                      Save this token now. You will not be able to view it again.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newPATValue}
                        readOnly
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background font-mono text-sm"
                      />
                      <button
                        onClick={handleCopyNewPAT}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                      >
                        {tokenCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {tokenCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_220px_auto] items-end">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Token name</label>
                    <input
                      type="text"
                      value={newPATName}
                      onChange={(e) => setNewPATName(e.target.value)}
                      placeholder="Example: automation-script"
                      maxLength={255}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Expiration</label>
                    <select
                      value={newPATExpiresInDays}
                      onChange={(e) => setNewPATExpiresInDays(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {PAT_EXPIRATION_OPTIONS.map((option) => (
                        <option key={option.label} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleCreatePAT}
                    disabled={!newPATName.trim() || saving === 'pat-create'}
                    className="h-10 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {saving === 'pat-create' ? 'Generating...' : 'Generate Token'}
                  </button>
                </div>

                <div className="space-y-3">
                  {personalAccessTokens.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No API access tokens yet.</p>
                  ) : (
                    personalAccessTokens.map((token) => (
                      <div key={token.id} className="p-3 rounded-lg border border-border bg-secondary/30">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-1">
                            <p className="font-medium">{token.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{token.token_prefix}••••••••</p>
                          </div>
                          <ConfirmDialog
                            trigger={
                              <button
                                disabled={saving === `pat-revoke-${token.id}`}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                Revoke
                              </button>
                            }
                            title="Revoke Token"
                            description={`Revoke token "${token.name}"? This action cannot be undone.`}
                            confirmLabel="Revoke"
                            variant="destructive"
                            onConfirm={async () => {
                              setSaving(`pat-revoke-${token.id}`)
                              setError(null)
                              try {
                                await revokePersonalAccessToken(token.id)
                                const patsData = await listPersonalAccessTokens()
                                setPersonalAccessTokens(patsData)
                                setSuccess('API access token revoked')
                                setTimeout(() => setSuccess(null), 3000)
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Failed to revoke token')
                              } finally {
                                setSaving(null)
                              }
                            }}
                          />
                        </div>
                        <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
                          <p>Created: {formatTimestamp(token.created_at)}</p>
                          <p>Last used: {formatTimestamp(token.last_used_at)}</p>
                          <p>Expires: {formatTimestamp(token.expires_at, 'Never')}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Use `X-API-Key: cc_...` or `Authorization: Bearer cc_...` when calling the API.{' '}
                <a href="/api/docs" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  Open Swagger docs
                </a>
                .
              </p>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="pt-1 border-t border-border/60">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notifications</p>
        </div>

        {/* Push Notifications toggle -- only show if browser supports push */}
        {pushSupported && (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-secondary">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Push Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    {pushLoading
                      ? 'Connecting…'
                      : pushNeedsInstall
                        ? 'Install the app first to enable push notifications'
                        : pushPermission === 'denied'
                          ? (isIOS()
                              ? 'Notifications blocked — re-enable in iPhone Settings → Notifications → CC Platform'
                              : "Notifications blocked — re-enable in your browser's site settings (click the lock icon in the address bar)")
                          : 'Get notified about builds, shares, and more'}
                  </p>
                  {pushError && (
                    <p className="text-sm text-destructive mt-1 font-medium">{pushError}</p>
                  )}
                </div>
              </div>
              <Switch
                checked={pushEnabled}
                onCheckedChange={(checked) => checked ? enablePush() : disablePush()}
                disabled={pushLoading || pushNeedsInstall || pushPermission === 'denied'}
              />
            </div>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Bell className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold flex items-center gap-2">
                  Notification Preferences
                  {notifSaved && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Saved
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Choose which notifications you receive
                </p>
              </div>
            </div>
            {showNotifications ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {showNotifications && (
            <div className="p-4 pt-0 border-t border-border">
              <div className="pt-4 space-y-6">
                {/* User notifications */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Your Apps</h4>
                  <div className="space-y-2">
                    {USER_NOTIFICATION_TYPES.map(({ type, label, highPriority }) => (
                      <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{label}</span>
                          {highPriority && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                              <AlertTriangle className="w-3 h-3" />
                              High priority
                            </span>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={notifPrefs[type] !== false}
                          onChange={(e) => handleNotifToggle(type, e.target.checked)}
                          disabled={notifSaving}
                          className="w-4 h-4 rounded border-border"
                        />
                      </label>
                    ))}
                  </div>
                </div>

                {/* Admin notifications */}
                {user.isAdmin && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      Admin
                      <Lock className="w-3 h-3" />
                    </h4>
                    <div className="space-y-2">
                      {ADMIN_NOTIFICATION_TYPES.map(({ type, label, highPriority }) => (
                        <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{label}</span>
                            {highPriority && (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                                <AlertTriangle className="w-3 h-3" />
                                High priority
                              </span>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={notifPrefs[type] !== false}
                            onChange={(e) => handleNotifToggle(type, e.target.checked)}
                            disabled={notifSaving}
                            className="w-4 h-4 rounded border-border"
                          />
                        </label>
                      ))}
                    </div>

                    <h5 className="text-xs font-medium text-muted-foreground mt-4 mb-2 uppercase tracking-wider">Platform Monitoring</h5>
                    <div className="space-y-2">
                      {ADMIN_MONITORING_TYPES.map(({ type, label, description, highPriority }) => (
                        <label key={type} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{label}</span>
                              {highPriority && (
                                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                                  <AlertTriangle className="w-3 h-3" />
                                  High priority
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">{description}</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={notifPrefs[type] === true}
                            onChange={(e) => handleNotifToggle(type, e.target.checked)}
                            disabled={notifSaving}
                            className="w-4 h-4 rounded border-border"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-muted-foreground">
                Notifications are saved automatically when toggled.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
